import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, XCircle, MoveRight, Search, Eye } from "lucide-react";
import AdminRecordDrawer from "@/pages/admin/AdminRecordDrawer";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "new", label: "New" },
  { key: "in_review", label: "In review" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
  { key: "spam_review", label: "Auto-flagged" },
];
const STATUS_TONE = {
  new: "bg-amber-50 text-amber-800 border-amber-200",
  in_review: "bg-sky-50 text-sky-800 border-sky-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  declined: "bg-rose-50 text-rose-800 border-rose-200",
  spam_review: "bg-orange-50 text-orange-800 border-orange-200",
};

export default function AdminApplicationsTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [approveFor, setApproveFor] = useState(null);
  const [pwd, setPwd] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setError("");
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/admin/partner-applications", { params });
      setData(data);
    } catch (e) { setError(e.response?.data?.detail || "Load error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setAppStatus = async (id, newStatus) => {
    setBusy(true);
    try { await api.post(`/admin/partner-applications/${id}/status`, { status: newStatus }); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const approve = async () => {
    if (!approveFor || pwd.length < 6) return;
    setBusy(true); setError(""); setOkMsg("");
    try {
      const { data } = await api.post(`/admin/partner-applications/${approveFor.id}/approve`, { initial_password: pwd });
      setOkMsg(`Vendor created · share: ${data.email} / ${data.initial_password}`);
      setApproveFor(null); setPwd("");
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Approval failed"); } finally { setBusy(false); }
  };

  const rows = (data?.applications || []).filter(a => {
    if (!q) return true;
    const n = q.toLowerCase();
    return a.farm_name?.toLowerCase().includes(n) || a.owner_name?.toLowerCase().includes(n)
      || a.email?.toLowerCase().includes(n) || a.location?.toLowerCase().includes(n);
  });

  return (
    <div className="space-y-4" data-testid="admin-applications-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">Partner applications</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">Inbound farm applications from <code className="font-mono text-xs">/partners/apply</code>. Approve to mint a vendor account.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, location" className="input-base pl-9 text-sm w-72" data-testid="apps-search"/>
        </div>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="apps-error">{error}</div>}
      {okMsg && <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="apps-success">{okMsg}</div>}

      <div className="flex gap-2 flex-wrap" data-testid="apps-status-filters">
        {STATUS_FILTERS.map(f => (
          <button key={f.key || "all"} onClick={() => setStatus(f.key)} data-testid={`apps-status-${f.key || "all"}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${status === f.key ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
            {f.label}{data?.counts && f.key ? ` · ${data.counts[f.key] || 0}` : ""}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="apps-list">
        {!data && <p className="card-flat p-8 text-sm text-[var(--ff-text-muted)]">Loading…</p>}
        {data && rows.length === 0 && <p className="card-flat p-8 text-sm text-[var(--ff-text-muted)]">No applications match.</p>}
        {rows.map(a => (
          <div key={a.id} className="card-flat p-5" data-testid={`app-${a.id}`}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-display text-lg">{a.farm_name}</p>
                  <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full border ${STATUS_TONE[a.status] || ""}`} data-testid={`app-status-${a.id}`}>{a.status}</span>
                  {a.honeypot_flagged && (
                    <span className="text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full border bg-orange-50 text-orange-800 border-orange-200" title="Auto-flagged by the spam filter — review before approving. Likely a legit applicant whose browser autofilled a hidden field." data-testid={`app-flagged-${a.id}`}>⚠ auto-flagged</span>
                  )}
                  <span className="text-xs text-[var(--ff-text-muted)]">{a.location}</span>
                </div>
                <p className="text-sm text-[var(--ff-text-secondary)] mt-1">{a.owner_name} · {a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
                {a.categories?.length > 0 && (
                  <div className="mt-2 flex gap-1.5 flex-wrap">
                    {a.categories.map(c => <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{c}</span>)}
                  </div>
                )}
                <p className="mt-3 text-sm text-[var(--ff-text-secondary)] whitespace-pre-wrap">{a.story}</p>
                <p className="text-xs text-[var(--ff-text-muted)] mt-3">
                  Received {new Date(a.created_at).toLocaleString()}
                  {a.farm_size_acres ? ` · ${a.farm_size_acres} acres` : ""}
                  {a.annual_volume_estimate ? ` · est. ${a.annual_volume_estimate}` : ""}
                  {a.referral_source ? ` · via ${a.referral_source}` : ""}
                </p>
                {a.website_url && <a href={a.website_url} target="_blank" rel="noreferrer" className="text-xs underline text-[var(--ff-primary)] mt-1 inline-block">Website ↗</a>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setDetail(a)} className="btn-secondary text-sm" data-testid={`app-details-${a.id}`}>
                  <Eye size={14}/> Full details
                </button>
                {(a.status === "new" || a.status === "spam_review") && (
                  <button disabled={busy} onClick={() => setAppStatus(a.id, "in_review")} className="btn-secondary text-sm" data-testid={`app-review-${a.id}`}>
                    <MoveRight size={14}/> Start review
                  </button>
                )}
                {(a.status === "new" || a.status === "in_review" || a.status === "spam_review") && (
                  <button disabled={busy} onClick={() => setApproveFor(a)} className="btn-primary text-sm" data-testid={`app-approve-open-${a.id}`}>
                    <ShieldCheck size={14}/> Approve
                  </button>
                )}
                {(a.status === "new" || a.status === "in_review" || a.status === "spam_review") && (
                  <button disabled={busy} onClick={() => setAppStatus(a.id, "declined")} className="btn-secondary text-sm text-rose-700" data-testid={`app-decline-${a.id}`}>
                    <XCircle size={14}/> Decline
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {approveFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setApproveFor(null)} data-testid="approve-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <p className="overline">Approve application</p>
            <h3 className="font-display text-2xl mt-1">{approveFor.farm_name}</h3>
            <p className="text-sm text-[var(--ff-text-muted)] mt-1">{approveFor.email}</p>
            <p className="mt-4 text-sm">Approving this application creates a <strong>pending_invite</strong> vendor + farm_admin user. Set an initial password to share with the owner.</p>
            <label className="block mt-4">
              <span className="overline">Initial password</span>
              <input type="text" minLength={6} className="input-base mt-2" placeholder="6+ chars" value={pwd} onChange={e => setPwd(e.target.value)} data-testid="approve-pwd"/>
            </label>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => { setApproveFor(null); setPwd(""); }} className="btn-secondary text-sm">Cancel</button>
              <button onClick={approve} disabled={busy || pwd.length < 6} className="btn-primary text-sm" data-testid="approve-confirm">{busy ? "Creating…" : "Create vendor & share creds"}</button>
            </div>
          </div>
        </div>
      )}

      <AdminRecordDrawer record={detail} title={detail?.farm_name ? `${detail.farm_name} — application` : "Application"} onClose={() => setDetail(null)} />
    </div>
  );
}
